import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { GOKAI } from "../components/GokaiBanner";
import { Enso, FlowerOfLife } from "../components/SacredGeometry";
import { Sparkles, Flame, Sun, Compass, Scroll, Check, Heart } from "lucide-react";

const ICONS = { Sparkles, Flame, Sun, Compass };
const CHAKRAS = [
  { name: "Root", jp: "地", color: "#c0392b" },
  { name: "Sacral", jp: "水", color: "#e67e22" },
  { name: "Solar", jp: "火", color: "#f1c40f" },
  { name: "Heart", jp: "風", color: "#5E8271" },
  { name: "Throat", jp: "空", color: "#2980b9" },
  { name: "Third Eye", jp: "識", color: "#34495e" },
  { name: "Crown", jp: "光", color: "#8e44ad" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [energy, setEnergy] = useState(() => Number(localStorage.getItem("reiki-energy") || 60));
  const [gokaiChecked, setGokaiChecked] = useState(() => JSON.parse(localStorage.getItem("reiki-gokai") || "[]"));

  useEffect(() => { api.get("/courses").then((r) => setCourses(r.data)).catch(() => {}); }, []);

  const completed = new Set(user?.progress?.completed_lessons || []);
  const certificates = user?.progress?.certificates || [];
  const active = user?.subscription?.status === "active" || user?.role === "admin";

  const toggleGokai = (i) => {
    const next = gokaiChecked.includes(i) ? gokaiChecked.filter((x) => x !== i) : [...gokaiChecked, i];
    setGokaiChecked(next);
    localStorage.setItem("reiki-gokai", JSON.stringify(next));
  };
  const setEnergyLevel = (v) => { setEnergy(v); localStorage.setItem("reiki-energy", String(v)); };

  return (
    <div className="min-h-screen pt-28 pb-24 bg-sanctuary-deep">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-center gap-4 mb-12">
          <Enso className="w-12 h-12 text-gold" />
          <div>
            <div className="eyebrow mb-1">Your Energy Altar</div>
            <h1 className="font-display text-4xl text-parchment font-light">Welcome, {user?.name}</h1>
          </div>
          {!active && (
            <Link to="/membership" className="ml-auto px-6 py-3 rounded-full bg-gold text-sanctuary-deep font-medium hover:bg-gold-hover transition-colors" data-testid="dashboard-upgrade">
              Unlock full access
            </Link>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Chakra energy check-in */}
          <div className="lg:col-span-2 rounded-2xl bg-sanctuary-card gold-border p-8" data-testid="chakra-checkin">
            <div className="flex items-center gap-2 eyebrow mb-6"><Heart size={14} /> Daily Energy Check-in</div>
            <div className="flex justify-between items-end gap-2 mb-6">
              {CHAKRAS.map((c, i) => {
                const lit = (energy / 100) * CHAKRAS.length > i;
                return (
                  <div key={c.name} className="flex-1 flex flex-col items-center gap-2">
                    <div className="w-full rounded-full transition-all duration-500" style={{ height: `${28 + i * 8}px`, background: lit ? c.color : "rgba(255,255,255,0.06)", boxShadow: lit ? `0 0 16px ${c.color}66` : "none" }} />
                    <span className="font-jp text-xs text-text-muted">{c.jp}</span>
                  </div>
                );
              })}
            </div>
            <input type="range" min="0" max="100" value={energy} onChange={(e) => setEnergyLevel(Number(e.target.value))}
              className="w-full accent-gold" data-testid="energy-slider" />
            <p className="text-center text-text-muted text-sm mt-3">How present does your energy feel today? — <span className="text-gold">{energy}%</span></p>
          </div>

          {/* Gokai tracker */}
          <div className="rounded-2xl bg-sanctuary-card gold-border p-8" data-testid="gokai-tracker">
            <div className="eyebrow mb-6">Today's Gokai</div>
            <div className="space-y-3">
              {GOKAI.map((g, i) => (
                <button key={i} onClick={() => toggleGokai(i)} className="w-full flex items-start gap-3 text-left group" data-testid={`gokai-check-${i}`}>
                  <span className={`w-5 h-5 rounded-full border grid place-items-center shrink-0 mt-0.5 transition-colors ${gokaiChecked.includes(i) ? "bg-gold border-gold text-sanctuary-deep" : "border-gold/40 text-transparent"}`}>
                    <Check size={12} />
                  </span>
                  <span className={`text-sm transition-colors ${gokaiChecked.includes(i) ? "text-parchment/60 line-through" : "text-text-muted group-hover:text-parchment"}`}>{g.en.replace("Just for today, ", "")}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Progress across courses */}
        <div className="eyebrow mt-14 mb-5">Your Path</div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {courses.map((c, i) => {
            const Icon = ICONS[c.icon] || Sparkles;
            const total = c.lesson_count || 0;
            return (
              <Link key={c.id} to={`/courses/${c.id}`} className="rounded-2xl bg-sanctuary-card gold-border p-6 hover:border-gold/40 transition-colors" data-testid={`dashboard-course-${i + 1}`}>
                <div className="w-10 h-10 rounded-full bg-gold/10 grid place-items-center text-gold mb-4"><Icon size={18} /></div>
                <div className="eyebrow mb-1">{c.level_label}</div>
                <div className="font-display text-xl text-parchment mb-3">{c.title}</div>
                <div className="text-sm text-text-muted">{total} teachings</div>
              </Link>
            );
          })}
        </div>

        {/* Certificates */}
        <div className="eyebrow mt-14 mb-5">Your Seals of Attunement</div>
        {certificates.length === 0 ? (
          <div className="rounded-2xl bg-sanctuary-card gold-border p-10 text-center">
            <FlowerOfLife className="w-14 mx-auto text-gold/30 mb-4" />
            <p className="text-text-muted">No seals yet. Complete a degree and its ceremony to receive your first certificate.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="certificates-grid">
            {certificates.map((cert) => (
              <Link key={cert.course_id} to={`/certificate/${cert.course_id}`} className="rounded-2xl bg-gold/5 border border-gold/30 p-6 hover:bg-gold/10 transition-colors">
                <Scroll className="w-8 h-8 text-gold mb-4" />
                <div className="font-display text-lg text-parchment">{cert.course_title}</div>
                <div className="text-xs text-text-muted mt-1">{cert.date}</div>
                <div className="text-xs text-gold mt-3">{cert.seal_code}</div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
